import React, { useEffect, useState } from "react";
import { adminGetSettings, adminSaveSettings } from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Circle, Save } from "lucide-react";
import TelegramRecipientsCard from "@/components/site/TelegramRecipientsCard";

const FIELDS = [
    { key: "phone", label: "Public phone", placeholder: "+44 20 ..." },
    { key: "email", label: "Public email", placeholder: "hello@acumenfs.co.uk" },
    { key: "office_hours", label: "Office hours", placeholder: "Mon–Fri 9am–6pm" },
    { key: "fca_number", label: "FCA registration number", placeholder: "e.g. 123456" },
    { key: "adviser_name", label: "Chat adviser display name", placeholder: "Acumen Adviser" },
];

export default function AdminSettings() {
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        adminGetSettings().then(setForm).catch(() => toast.error("Could not load settings"));
    }, []);

    if (!form) return <div className="text-brand-muted">Loading…</div>;

    const save = async () => {
        setSaving(true);
        try {
            await adminSaveSettings(form);
            toast.success("Settings saved.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to save");
        } finally {
            setSaving(false);
        }
    };

    const set = (k, v) => setForm({ ...form, [k]: v });

    return (
        <div data-testid="admin-settings" className="space-y-10 max-w-3xl">
            <div>
                <div className="text-xs uppercase tracking-[0.22em] text-brand-accent font-bold">
                    Site configuration
                </div>
                <h1 className="font-serif-editorial text-4xl text-brand-primary mt-1">
                    Settings
                </h1>
                <p className="text-sm text-brand-muted mt-2">
                    Contact details below appear site-wide. Leave empty to hide.
                </p>
            </div>

            <section className="bg-brand-surface border border-brand-border p-6 space-y-5">
                <h2 className="font-bold text-brand-primary">Contact details</h2>
                {FIELDS.map((f) => (
                    <Field
                        key={f.key}
                        label={f.label}
                        value={form[f.key] || ""}
                        onChange={(v) => set(f.key, v)}
                        placeholder={f.placeholder}
                        testid={`settings-${f.key}`}
                    />
                ))}
                <div>
                    <label className="text-xs uppercase tracking-[0.18em] text-brand-muted font-bold">
                        Address
                    </label>
                    <textarea
                        rows={3}
                        value={form.address || ""}
                        onChange={(e) => set("address", e.target.value)}
                        data-testid="settings-address"
                        placeholder="Street, city, postcode"
                        className="mt-2 w-full bg-transparent border-b border-brand-border py-3 text-lg text-brand-primary focus:border-brand-primary outline-none resize-none"
                    />
                </div>
            </section>

            <section className="bg-brand-surface border border-brand-border p-6 space-y-5">
                <h2 className="font-bold text-brand-primary">Live chat</h2>
                <div className="flex items-center justify-between gap-4 p-4 border border-brand-border bg-brand-bg/50">
                    <div>
                        <div className="flex items-center gap-2 text-sm font-bold text-brand-primary">
                            <Circle
                                size={10}
                                className={form.chat_online ? "text-[#2E8B56]" : "text-[#B33B3B]"}
                                fill="currentColor"
                            />
                            Adviser status: {form.chat_online ? "Online" : "Offline"}
                        </div>
                        <div className="text-xs text-brand-muted mt-1">
                            Visitors see a green dot when online, red when offline.
                        </div>
                    </div>
                    <Switch
                        checked={!!form.chat_online}
                        onCheckedChange={(v) => set("chat_online", v)}
                        data-testid="settings-chat-online"
                    />
                </div>
                <Field
                    label="Chat welcome message"
                    value={form.chat_welcome || ""}
                    onChange={(v) => set("chat_welcome", v)}
                    placeholder="Hi — leave a message and an adviser will reply."
                    testid="settings-chat-welcome"
                />
            </section>

            <TelegramRecipientsCard />

            <button
                onClick={save}
                disabled={saving}
                data-testid="settings-save"
                className="inline-flex items-center gap-2 bg-brand-primary text-brand-bg px-8 py-4 text-sm font-bold tracking-wide hover:bg-brand-primaryHover transition-colors disabled:opacity-60"
            >
                <Save size={14} /> {saving ? "Saving…" : "Save changes"}
            </button>
        </div>
    );
}

function Field({ label, value, onChange, placeholder, testid }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-[0.18em] text-brand-muted font-bold">
                {label}
            </label>
            <input
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                data-testid={testid}
                className="mt-2 w-full bg-transparent border-b border-brand-border py-3 text-lg text-brand-primary focus:border-brand-primary outline-none"
            />
        </div>
    );
}
